import { useEffect, useMemo, useRef, useState } from 'react';
// import PhaseIcon from '~/components/PhaseIcon/PhaseIcon';
import { PhaseIcon } from '@pasqal/core/ui/components/pulser-studio/components/PhaseIcon';
import EditorButton from '~/components/editor/EditorButton/EditorButton';
import EditorSelect from '~/components/editor/EditorSelect/EditorSelect';
import EditorSlider from '~/components/editor/EditorSlider/EditorSlider';
import Collapsible from '~/components/widgets/Collapsible/Collapsible';
import { useExperiment, waveformsDuration } from '~/systems/experiment';
import { assert } from '~/util';
import { useRaf } from '~/util/animation';
import WaveformProperties from '../WaveformProperties/WaveformProperties';
import type { Pulse, Waveform } from '~/models/studio';
import type { ExperimentTx } from '~/systems/experiment';
import './PulseProperties.css';

interface PulsePropertiesProps {
  pulseUuid: string;
  pulse: Pulse;
  selectedPulse?: boolean;
}

export default function PulseProperties({
  pulseUuid,
  pulse,
  selectedPulse
}: PulsePropertiesProps) {
  const {
    atoms,
    channels,
    commitedPulses,
    computedPulses,
    device,
    openTransaction,
    pulses
  } = useExperiment();

  const txRef = useRef<ExperimentTx | null>(null);
  const cmdIdxRef = useRef(0);

  function getEditTx() {
    if (txRef.current == null || !txRef.current.isOpen()) {
      txRef.current = openTransaction('pulseEdit');
      cmdIdxRef.current = txRef.current.push({
        kind: 'update_pulse',
        uuid: pulse.uuid,
        props: {}
      });
    }
    return txRef.current;
  }

  function updatePulse(
    propsFn: (pulse: Pulse) => Partial<Omit<Pulse, 'uuid'>>
  ) {
    getEditTx().update(cmdIdxRef.current, (cmd) => {
      assert(cmd.kind === 'update_pulse');
      const p = commitedPulses.find((p) => p.uuid === cmd.uuid);
      if (p == null) {
        return cmd;
      }
      return { ...cmd, props: propsFn(p) };
    });
  }

  function removePulse() {
    const tx = openTransaction('removePulse');
    tx.push({ kind: 'remove_pulse', uuid: pulse.uuid });
    tx.commit();
  }

  function commit() {
    if (txRef.current != null && txRef.current.isOpen()) {
      txRef.current.commit();
    }
  }

  useEffect(() => () => commit(), []);

  const pulseIdx = useMemo(() => {
    return pulses.findIndex((p) => p.uuid === pulseUuid);
  }, [pulseUuid, pulses]);

  const pulseLayout = useMemo(() => {
    return computedPulses[pulseIdx]?.layout;
  }, [computedPulses, pulseIdx]);

  const ampDuration = waveformsDuration(pulse.amplitude);
  const detDuration = waveformsDuration(pulse.detuning ?? []);

  const pulseChannel = useMemo(() => {
    const channel = channels.find((ch) => ch.uuid === pulse.channelUuid);
    assert(channel != null);
    return channel;
  }, [channels, pulse.channelUuid]);

  function updateAmplitude(index: number, waveform: Waveform) {
    updatePulse((p) => {
      const amplitude = [...p.amplitude];
      amplitude[index] = waveform;
      return { amplitude };
    });
  }

  function updateDetuning(index: number, waveform: Waveform) {
    updatePulse((p) => {
      const detuning = [...(p.detuning ?? [])];
      detuning[index] = waveform;
      return { detuning };
    });
  }

  function updatePulseData(update: Partial<Pulse>, commitImmediately = false) {
    updatePulse(() => update);
    if (commitImmediately) {
      commit();
    }
  }

  const targetOptions = useMemo(
    () => atoms.map((atom) => ({ label: atom.label, value: atom.uuid })),
    [atoms]
  );

  const [atomSelectActive, setAtomSelectActive] = useState(false);
  const atomSelectContainerRef = useRef<HTMLDivElement>(null);
  const atomSelectContainerTop = useRef(0);

  useRaf(atomSelectActive, () => {
    const node = atomSelectContainerRef.current;
    atomSelectContainerTop.current = node?.getBoundingClientRect().top ?? 0;
  });

  const [collapsibleOpened, setCollapsibleOpened] = useState(!!selectedPulse);

  // Automatically add a UUID to field names to prevent duplicates
  const generateFieldName = (label: string) => {
    return `${label}-${pulseUuid}`;
  };

  if (pulseIdx < 0 || pulseLayout == null) {
    return null;
  }

  return (
    <div className={`PulseProperties${collapsibleOpened ? ' is-opened' : ''}`}>
      <Collapsible
        initialState={selectedPulse}
        subcollapsible
        text={() => (
          <h3 className="PulseProperties-collapsibleTitle">
            Pulse {pulseIdx + 1}
          </h3>
        )}
        onUpdateOpened={(opened) => setCollapsibleOpened(opened)}
        items={
          <div className="PulseProperties-items">
            {pulse.targetUuid !== null && (
              <div
                ref={atomSelectContainerRef}
                className="PulseProperties-atomSelectContainer"
              >
                <EditorSelect
                  label="Target"
                  name={generateFieldName('target')}
                  value={pulse.targetUuid}
                  options={targetOptions}
                  disabled={targetOptions.length <= 1}
                  onChange={(v) => updatePulseData({ targetUuid: v }, true)}
                  onStateChange={(active) => {
                    // nextTick equivalent
                    setTimeout(() => setAtomSelectActive(active), 0);
                  }}
                />
              </div>
            )}
            <h4 className="PulseProperties-waveformHeader">
              Amplitude (rad/μs)
            </h4>
            {pulse.amplitude.map((_, index) => {
              const livePulse = pulses[pulseIdx];
              const liveWaveform = livePulse?.amplitude[index];
              if (liveWaveform === null) {
                return null;
              }
              return (
                <WaveformProperties
                  key={index}
                  id={`amplitude-${index}-${pulseUuid}`}
                  sequenceDuration={ampDuration}
                  waveform={liveWaveform}
                  isDetuning={false}
                  device={device}
                  channel={pulseChannel}
                  onUpdate={(wf) => updateAmplitude(index, wf)}
                  onCommit={commit}
                />
              );
            })}
            <div className="PulseProperties-phaseRow">
              <div className="PulseProperties-phaseEditor">
                <EditorSlider
                  label="Phase"
                  name={generateFieldName('phase')}
                  value={pulse.phase / Math.PI}
                  min={0}
                  max={2}
                  step={0.01}
                  snapRange={0.03}
                  divisions={[0, 0.5, 1, 1.5, 2]}
                  unit="𝝅 rad"
                  onChange={(v) => updatePulseData({ phase: v * Math.PI })}
                  onBlur={commit}
                />
              </div>
              <svg width={40} height={40} viewBox="-20 -20 40 40">
                <PhaseIcon
                  pos={{ x: 0, y: 0 }}
                  radius={18}
                  phase={pulse.phase}
                  phaseOffset={pulseLayout.phaseOffset}
                />
              </svg>
            </div>
            <div className="PulseProperties-phaseRow">
              <div className="PulseProperties-phaseEditor">
                <EditorSlider
                  label="Post phase shift"
                  name={generateFieldName('post-phase-shift')}
                  value={pulse.postPhaseShift / Math.PI}
                  min={0}
                  max={2}
                  step={0.01}
                  divisions={[0, 0.5, 1, 1.5, 2]}
                  snapRange={0.03}
                  unit="𝝅 rad"
                  onChange={(v) =>
                    updatePulseData({ postPhaseShift: v * Math.PI })
                  }
                  onBlur={commit}
                />
              </div>
              <svg width={40} height={40} viewBox="-20 -20 40 40">
                <PhaseIcon
                  pos={{ x: 0, y: 0 }}
                  radius={18}
                  phase={pulse.postPhaseShift}
                  phaseOffset={pulseLayout.phaseOffset}
                />
              </svg>
            </div>
            {pulse.detuning != null && (
              <>
                <h3 className="PulseProperties-waveformHeader">
                  Detuning (rad/μs)
                </h3>
                {pulse.detuning.map((waveform, index) => (
                  <WaveformProperties
                    key={index}
                    id={`pulse-detuning-${index}-${pulseUuid}`}
                    sequenceDuration={detDuration}
                    waveform={waveform}
                    isDetuning
                    device={device}
                    channel={pulseChannel}
                    onUpdate={(wf) => updateDetuning(index, wf)}
                    onCommit={commit}
                  />
                ))}
              </>
            )}
            <EditorButton
              className="PulseProperties-removePulseButton"
              onClick={removePulse}
            >
              Remove pulse
            </EditorButton>
          </div>
        }
      />
    </div>
  );
}
